import { useQueryClient } from '@tanstack/react-query';
import { Bell, Home, Inbox, Keyboard, LayoutGrid, LogOut, Menu as MenuIcon, Monitor, Moon, Plus, Search, ShieldCheck, Sun, Target, WifiOff } from 'lucide-react';
import { useEffect, type ReactNode } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router';
import { patch, post } from '../api/client';
import { useDashboard, useNotifications, useSavedViews } from '../api/queries';
import { useSession } from '../api/session';
import { useLiveUpdates } from '../api/live';
import { Button, Menu } from '../components/ui';
import { FollowUpDialog, SnoozeDialog } from '../features/tasks/Dialogs';
import { QuickAdd } from '../features/tasks/QuickAdd';
import { SearchPalette } from '../features/tasks/SearchPalette';
import { TaskFormModal } from '../features/tasks/TaskForm';
import { formatRelativeTime } from '../lib/format';
import { useOnline } from '../lib/hooks';
import { applyTheme } from '../lib/theme';
import { NAV_KEYS, ShortcutsHelp, useGlobalShortcuts, useShortcutHints } from './shortcuts';
import { useUI } from './ui';

function NavItem({ to, emoji, label, count, alert, end }: { to: string; emoji: string; label: string; count?: number; alert?: boolean; end?: boolean }) {
  const ui = useUI();
  const [hints] = useShortcutHints();
  const key = NAV_KEYS[to];
  return (
    <NavLink to={to} end={end} className="nav-item" onClick={() => ui.setNavOpen(false)} title={key ? `${label} (${key})` : undefined} aria-keyshortcuts={key}>
      <span className="nav-emoji" aria-hidden>
        {emoji}
      </span>
      <span className="truncate">{label}</span>
      {!!count && (
        <span className={`nav-count ${alert ? 'alert' : ''}`} aria-label={`${count} items`}>
          {count > 99 ? '99+' : count}
        </span>
      )}
      {hints && key && (
        <kbd className={`kbd nav-kbd ${count ? '' : 'solo'}`} aria-hidden>
          {key}
        </kbd>
      )}
    </NavLink>
  );
}

function Sidebar() {
  const { me, can } = useSession();
  const ui = useUI();
  const dash = useDashboard();
  const views = useSavedViews();
  const c = dash.data?.counts;
  return (
    <>
      {ui.navOpen && <div className="scrim" onClick={() => ui.setNavOpen(false)} aria-hidden />}
      <nav className={`sidebar ${ui.navOpen ? 'open' : ''}`} aria-label="Main">
        <NavLink to="/" className="brand" end onClick={() => ui.setNavOpen(false)}>
          <img src="/favicon.svg" className="brand-logo" alt="" />
          Task OS
        </NavLink>
        <NavItem to="/" end emoji="🏠" label="Dashboard" />
        <NavItem to="/inbox" emoji="📥" label="Inbox" count={c?.inbox} />
        <NavItem to="/today" emoji="🎯" label="Today" count={c?.dueToday} />
        <NavItem to="/overdue" emoji="🚨" label="Overdue" count={c?.overdue} alert />
        <NavItem to="/followups" emoji="📌" label="Follow-ups" count={c?.followUps} />
        <NavItem to="/upcoming" emoji="📅" label="Upcoming" />
        <NavItem to="/waiting" emoji="⏳" label="Waiting" count={c?.waiting} />
        <NavItem to="/recurring" emoji="🔁" label="Recurring" />
        <NavItem to="/mine" emoji="🙋" label="Assigned to me" />
        {can('task.view_team') && <NavItem to="/team" emoji="👥" label="Team" count={c?.staff} />}
        <NavItem to="/presets" emoji="📌" label="Presets" />
        {me.approvalModuleEnabled && can('approval.request') && <NavItem to="/approvals" emoji="✅" label="Approvals" />}
        <NavItem to="/history" emoji="📊" label="History" />
        {!!views.data?.length && (
          <>
            <div className="nav-section">Saved views</div>
            {views.data.map((v) => (
              <NavItem key={v.id} to={`/views/${v.id}`} emoji="⭐" label={v.name} />
            ))}
          </>
        )}
        <div className="sidebar-footer">
          <NavItem to="/settings" emoji="⚙" label="Settings" />
          <button type="button" className="nav-item hide-touch" onClick={() => ui.setHelpOpen(true)} aria-keyshortcuts="?">
            <span className="nav-emoji">
              <Keyboard size={16} aria-hidden />
            </span>
            Keyboard shortcuts{' '}
            <kbd className="kbd" style={{ marginLeft: 'auto' }} aria-hidden>
              ?
            </kbd>
          </button>
        </div>
      </nav>
    </>
  );
}

function NotificationsBell() {
  const n = useNotifications();
  const qc = useQueryClient();
  const nav = useNavigate();
  const unread = n.data?.unread ?? 0;
  const items = (n.data?.items ?? []).slice(0, 12);
  const markAll = async () => {
    await post('/api/notifications/read', { ids: 'all' });
    void qc.invalidateQueries({ queryKey: ['notifications'] });
  };
  return (
    <Menu
      label="Notifications"
      items={[
        { label: `Notifications${unread ? ` (${unread} unread)` : ''}`, header: true as const },
        ...(items.length
          ? items.map((i) => ({
              label: (
                <span style={{ display: 'flex', flexDirection: 'column', fontWeight: i.readAt ? 400 : 650 }}>
                  <span className="truncate" style={{ maxWidth: 240 }}>
                    {i.title}
                  </span>
                  <span className="small muted">{formatRelativeTime(i.createdAt)}</span>
                </span>
              ),
              onSelect: async () => {
                if (!i.readAt) await post('/api/notifications/read', { ids: [i.id] });
                void qc.invalidateQueries({ queryKey: ['notifications'] });
                if (i.taskId) nav(`/tasks/${i.taskId}`);
                else if (i.approvalId) nav(`/approvals/${i.approvalId}`);
              },
            }))
          : [{ label: <span className="muted">You're all caught up 🎉</span>, onSelect: () => undefined, disabled: true }]),
        'sep' as const,
        { label: 'Mark all as read', onSelect: markAll, disabled: !unread },
      ]}
      trigger={(p) => (
        <span style={{ position: 'relative', display: 'inline-flex' }}>
          <Button variant="ghost" iconOnly icon={<Bell size={18} />} aria-label={`Notifications${unread ? `, ${unread} unread` : ''}`} {...p} />
          {unread > 0 && (
            <span className="nav-count alert" style={{ position: 'absolute', top: -2, right: -4, fontSize: '0.66rem', minWidth: 18, padding: '0 5px' }} aria-hidden>
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </span>
      )}
    />
  );
}

function UserMenu() {
  const { me } = useSession();
  const qc = useQueryClient();
  const nav = useNavigate();
  const ui = useUI();
  const setTheme = async (theme: 'LIGHT' | 'DARK' | 'SYSTEM') => {
    applyTheme(theme);
    await patch('/api/me/preferences', { theme }).catch(() => undefined);
    void qc.invalidateQueries({ queryKey: ['me'] });
  };
  return (
    <Menu
      label="Account"
      items={[
        { label: `${me.name} · ${me.role.toLowerCase()}`, header: true as const },
        {
          custom: (
            <div className="theme-switch" role="radiogroup" aria-label="Theme">
              {(
                [
                  ['LIGHT', 'Light', <Sun key="l" size={15} aria-hidden />],
                  ['DARK', 'Dark', <Moon key="d" size={15} aria-hidden />],
                  ['SYSTEM', 'System', <Monitor key="s" size={15} aria-hidden />],
                ] as const
              ).map(([v, label, icon]) => (
                <button key={v} type="button" role="radio" className="menu-item" aria-checked={me.preferences.theme === v} onClick={() => void setTheme(v)}>
                  {icon}
                  {label}
                </button>
              ))}
            </div>
          ),
        },
        'sep' as const,
        { label: 'My settings', onSelect: () => nav('/settings/profile') },
        { label: 'Security & PIN', icon: <ShieldCheck size={15} />, onSelect: () => nav('/settings/security') },
        { label: 'Keyboard shortcuts', icon: <Keyboard size={15} />, onSelect: () => ui.setHelpOpen(true), hint: '?' },
        {
          label: 'Sign out',
          icon: <LogOut size={15} />,
          onSelect: async () => {
            await post('/api/auth/logout').catch(() => undefined);
            qc.clear();
            window.location.assign('/login');
          },
        },
      ]}
      trigger={(p) => (
        <Button variant="ghost" aria-label="Account menu" {...p}>
          <span className="badge primary" aria-hidden>
            {me.name.slice(0, 1).toUpperCase()}
          </span>
          <span className="hide-mobile">{me.name}</span>
        </Button>
      )}
    />
  );
}

const TITLES: Record<string, string> = {
  '/': 'Dashboard',
  '/inbox': 'Inbox',
  '/today': 'Today',
  '/overdue': 'Overdue',
  '/followups': 'Follow-ups',
  '/upcoming': 'Upcoming',
  '/waiting': 'Waiting',
  '/recurring': 'Recurring',
  '/mine': 'Assigned to me',
  '/team': 'Team',
  '/presets': 'Presets',
  '/history': 'History',
  '/approvals': 'Approvals',
};

export function Shell({ children }: { children: ReactNode }) {
  const ui = useUI();
  const online = useOnline();
  const loc = useLocation();
  useGlobalShortcuts();
  useLiveUpdates(true);
  useEffect(() => {
    ui.setCurrent(null);
    window.scrollTo({ top: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc.pathname]);
  const title = TITLES[loc.pathname] ?? (loc.pathname.startsWith('/settings') ? 'Settings' : loc.pathname.startsWith('/tasks/') ? 'Task' : '');
  return (
    <div className="shell">
      <a href="#main" className="skip-link">
        Skip to content
      </a>
      <Sidebar />
      <div className="main">
        {!online && (
          <div className="offline-banner" role="status">
            <WifiOff size={16} aria-hidden /> Offline — changes can't be saved until you're back online. Your typed drafts are kept.
          </div>
        )}
        <header className="topbar">
          <Button className="hide-desktop" variant="ghost" iconOnly icon={<MenuIcon size={20} />} aria-label="Open menu" onClick={() => ui.setNavOpen(true)} />
          <span className="topbar-title hide-desktop truncate">{title}</span>
          <div className="hide-mobile" style={{ flex: 1, minWidth: 0, display: 'flex' }}>
            <QuickAdd />
          </div>
          <span className="spacer hide-desktop" />
          <Button variant="ghost" iconOnly icon={<Search size={18} />} aria-label="Search (S)" title="Search (S or /)" aria-keyshortcuts="S /" onClick={() => ui.setSearchOpen(true)} />
          <Button className="hide-mobile hide-touch" variant="ghost" iconOnly icon={<Keyboard size={18} />} aria-label="Keyboard shortcuts (?)" title="Keyboard shortcuts (?)" aria-keyshortcuts="?" onClick={() => ui.setHelpOpen(true)} />
          <Button className="hide-mobile" variant="primary" icon={<Plus size={16} />} onClick={() => ui.openTaskForm({ mode: 'create' })} title="New task (N)" aria-keyshortcuts="N">
            New task
            <kbd className="kbd kbd-on-primary hide-touch" aria-hidden>
              N
            </kbd>
          </Button>
          <NotificationsBell />
          <UserMenu />
        </header>
        <main id="main" className="content" tabIndex={-1}>
          <div className="hide-desktop mb">
            <QuickAdd />
          </div>
          {children}
        </main>
      </div>
      <nav className="bottom-nav" aria-label="Quick navigation">
        <NavLink to="/" end aria-label="Dashboard">
          <Home size={20} aria-hidden />
          Home
        </NavLink>
        <NavLink to="/today" aria-label="Today">
          <Target size={20} aria-hidden />
          Today
        </NavLink>
        <button type="button" className="fab" aria-label="New task" onClick={() => ui.openTaskForm({ mode: 'create' })}>
          <Plus size={24} aria-hidden />
        </button>
        <NavLink to="/inbox" aria-label="Inbox">
          <Inbox size={20} aria-hidden />
          Inbox
        </NavLink>
        <button type="button" aria-label="All pages" onClick={() => ui.setNavOpen(true)}>
          <LayoutGrid size={20} aria-hidden />
          Pages
        </button>
      </nav>
      <TaskFormModal />
      <SnoozeDialog />
      <FollowUpDialog />
      <SearchPalette />
      <ShortcutsHelp />
    </div>
  );
}
